import { useEffect, useState } from 'react';
import { useStore } from '@nanostores/react';
import { toasts, removeToast, type ToastType } from '@agentic/magento-client/stores/toast';
import { CheckCircle, XCircle, Info, AlertTriangle, X } from '../../icons/inline';

// Icon mapping for each toast type
const toastIcons: Record<ToastType, typeof CheckCircle> = {
  success: CheckCircle,
  error: XCircle,
  info: Info,
  warning: AlertTriangle,
};

// Color schemes for each toast type
// Toast backgrounds use CSS variables for admin-configurable success/error colors.
// The variable values default to the standard green/red.
const toastStyles: Record<ToastType, { bg: string; border: string; icon: string; cssVar?: string }> = {
  success: {
    bg: 'bg-green-500/10',
    border: 'border-green-500/30',
    icon: 'text-green-500',
    cssVar: '--color-toast-success-bg',
  },
  error: {
    bg: 'bg-red-500/10',
    border: 'border-red-500/30',
    icon: 'text-red-500',
    cssVar: '--color-toast-error-bg',
  },
  info: {
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/30',
    icon: 'text-blue-500',
    cssVar: '--color-info',
  },
  warning: {
    bg: 'bg-yellow-500/10',
    border: 'border-yellow-500/30',
    icon: 'text-yellow-500',
    cssVar: '--color-warning',
  },
};

interface ToastItemProps {
  type: ToastType;
  message: string;
  title?: string;
  id: string;
  action?: {
    label: string;
    onClick: () => void;
  };
}

function ToastItem({ type, message, title, id, action }: ToastItemProps) {
  const [isExiting, setIsExiting] = useState(false);
  const [progress, setProgress] = useState(100);
  const Icon = toastIcons[type];
  const styles = toastStyles[type];

  useEffect(() => {
    // Animate progress bar
    const duration = 5000; // 5 seconds
    const interval = 50; // Update every 50ms
    const step = 100 / (duration / interval);
    
    const timer = setInterval(() => {
      setProgress((prev) => {
        if (prev <= 0) {
          clearInterval(timer);
          return 0;
        }
        return prev - step;
      });
    }, interval);

    return () => clearInterval(timer);
  }, []);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => removeToast(id), 300);
  };

  const handleAction = () => {
    if (action) {
      action.onClick();
      handleClose();
    }
  };

  return (
    <div
      className={`
        relative w-full max-w-sm overflow-hidden
        glass-card ${styles.bg} border ${styles.border}
        shadow-lg shadow-black/20
        transform transition-all duration-300 ease-out
        ${isExiting ? 'translate-x-full opacity-0' : 'translate-x-0 opacity-100'}
      `}
      style={styles.cssVar ? { backgroundColor: `color-mix(in srgb, var(${styles.cssVar}) 15%, transparent)`, borderColor: `color-mix(in srgb, var(${styles.cssVar}) 30%, transparent)` } : undefined}
      role="alert"
      aria-live="polite"
    >
      {/* Progress bar */}
      <div className="absolute bottom-0 left-0 h-1 bg-current opacity-20 transition-all duration-100">
        <div 
          className="h-full bg-current transition-all duration-100"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="flex items-start gap-3 p-4">
        {/* Icon */}
        <div className={`flex-shrink-0 ${styles.icon}`} style={styles.cssVar ? { color: `var(${styles.cssVar})` } : undefined}>
          <Icon className="w-5 h-5" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {title && (
            <h4 className="text-sm font-semibold text-[var(--text-primary)] mb-1">
              {title}
            </h4>
          )}
          <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
            {message}
          </p>
          
          {/* Action button */}
          {action && (
            <button
              onClick={handleAction}
              className="mt-2 text-sm font-medium text-[var(--color-accent-fg)] hover:underline"
            >
              {action.label}
            </button>
          )}
        </div>

        {/* Close button */}
        <button
          onClick={handleClose}
          className="flex-shrink-0 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
          aria-label="Close notification"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export default function ToastContainer() {
  const toastList = useStore(toasts);

  if (toastList.length === 0) return null;

  // z-[10000] sits ABOVE the Help & Support widget (button z-index 9998, its open
  // panel 9999) so a toast — especially an add-to-cart error like "You need to
  // choose options" — is never hidden behind it. bottom-24 lifts the stack clear
  // of the bottom-right support button so both stay visible.
  return (
    <div
      className="fixed bottom-24 right-4 z-[10000] flex flex-col gap-3 pointer-events-none"
      aria-live="polite"
      aria-atomic="true"
    >
      {toastList.map((toast) => (
        <div key={toast.id} className="pointer-events-auto">
          <ToastItem
            type={toast.type}
            message={toast.message}
            title={toast.title}
            id={toast.id}
            action={toast.action}
          />
        </div>
      ))}
    </div>
  );
}

// Individual toast display component (for inline usage)
interface InlineToastProps {
  type: ToastType;
  message: string;
  onClose?: () => void;
}

export function InlineToast({ type, message, onClose }: InlineToastProps) {
  const Icon = toastIcons[type];
  const styles = toastStyles[type];

  return (
    <div
      className={`
        flex items-center gap-3 p-4
        ${styles.bg} border ${styles.border}
        animate-in fade-in slide-in-from-top-2 duration-300
      `}
      style={{ borderRadius: 'var(--style-card-radius, 12px)' }}
      role="alert"
    >
      <Icon className={`w-5 h-5 ${styles.icon}`} />
      <p className="flex-1 text-sm text-[var(--text-secondary)]">{message}</p>
      {onClose && (
        <button
          onClick={onClose}
          className="text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
